import { Box, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconBrandLinkedin, IconMail, IconMapPin } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import { useThemeSwitcher } from '../../contexts/ThemeContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { Container } from '../atomic';
import SecureEmail from '../SecureEmail';

/**
 * @typedef {Object} FooterLink
 * @property {string} text - Link text
 * @property {string} href - Link URL
 * @property {boolean} [comingSoon] - Whether this is coming soon
 * @property {boolean} [openInNewTab] - Whether to open the link in a new browser tab
 */

/**
 * @typedef {Object} FooterColumn
 * @property {string} title - Column title
 * @property {FooterLink[]} links - Column links
 */

/**
 * @typedef {Object} FooterNewProps
 * @property {FooterColumn[]} [columns] - Footer columns
 */

/**
 * Enhanced Footer Section with multi-column layout
 * Based on the design specification.
 * All colors sourced from MUI theme; image paths from themeConfig.
 *
 * @param {FooterNewProps} props - Component props
 * @returns {JSX.Element} Footer component
 */
function FooterNew({ columns }) {
  const navigate = useNavigate();
  const { trackEvent, trackCTA } = useAnalytics();
  const theme = useTheme();
  const { themeConfig } = useThemeSwitcher();

  /** @type {FooterColumn[]} */
  const defaultColumns = [
    {
      title: 'Resources',
      links: [
        {
          text: 'Documentation',
          href: 'https://rsm-public-assets.s3.us-east-2.amazonaws.com/EllaFin-Implementation-Guide-v2.1.pdf',
          openInNewTab: true,
        },
        { text: 'Support', href: '/early-access' },
      ],
    },
    {
      title: 'Product',
      links: [
        { text: 'About', href: '/about' },
        {
          text: 'eBook',
          href: 'https://rsmcollective.activehosted.com/content/2OBma0/2026/08/16/3ce8298f-4448-49ea-8e8c-b6e1282b3c7a.pdf',
          openInNewTab: true,
        },
      ],
    },
    {
      title: 'Legal',
      links: [
        { text: 'Privacy Policy', href: '/privacy' },
        { text: 'Terms of Service', href: '/terms' },
      ],
    },
  ];

  // Sort footer links: items without comingSoon appear first, then items with comingSoon
  const sortedFooterColumns = (cols) =>
    cols.map((column) => ({
      ...column,
      links: [
        ...column.links.filter((link) => !link.comingSoon),
        ...column.links.filter((link) => link.comingSoon),
      ],
    }));

  const footerColumns = columns
    ? sortedFooterColumns(columns)
    : sortedFooterColumns(defaultColumns);

  const handleLogoClick = () => {
    trackEvent('logo_click', {
      location: 'footer',
      action: 'navigate_home',
    });
    navigate('/');
  };

  /**
   * Handle link click
   * @param {string} href - Link URL
   * @param {string} linkText - Link text
   * @param {boolean} [comingSoon] - Whether this is coming soon
   */
  const handleLinkClick = (href, linkText, comingSoon = false) => {
    if (comingSoon) {
      return; // Don't navigate for coming soon items
    }
    trackCTA('footer_nav', linkText, {
      destination: href,
      section: 'footer_links',
    });
    navigate(href);
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.custom.footerBackground,
        color: 'white',
        // Olive stripe at top — matches header and CTA green (#779a0a)
        borderTop: `4px solid ${theme.palette.secondary.main}`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Main Footer Content */}
      <Container maxWidth="lg">
        <Box sx={{ py: { xs: 6, md: 8 } }}>
          <Grid
            container
            spacing={{ xs: 4, md: 6 }}
            justifyContent="space-between"
          >
            {/* Company Info Section */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  textAlign: { xs: 'center', md: 'left' },
                  alignItems: { xs: 'center', md: 'flex-start' },
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  component="img"
                  src={
                    themeConfig.assets?.footerLogo ||
                    `${process.env.PUBLIC_URL}/images/full-logo-white-green.png`
                  }
                  alt="EllaFin\u2122"
                  onClick={handleLogoClick}
                  sx={{
                    height: { xs: 40, md: 50 },
                    objectFit: 'contain',
                    objectPosition: { xs: 'center', md: 'left' },
                    mb: 3,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: 0.95,
                    '&:hover': {
                      opacity: 1,
                      transform: 'scale(1.02)',
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: 1.8,
                    fontSize: '0.95rem',
                    maxWidth: '300px',
                    mb: 3,
                  }}
                >
                  Finance-First FinOps. Aligning AWS cloud costs with financial
                  reporting for CFOs, FP&A teams, and FinOps practitioners.
                </Typography>

                {/* Contact Info */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    alignItems: { xs: 'center', md: 'flex-start' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconMail
                      size={18}
                      stroke={1.5}
                      color={theme.custom.cream}
                      style={{ flexShrink: 0 }}
                    />
                    <SecureEmail
                      emailKey="info"
                      component="span"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '0.9rem',
                        textDecoration: 'underline',
                        transition: 'color 0.2s',
                        '&:hover': {
                          color: 'rgba(255, 255, 255, 1)',
                        },
                        cursor: 'pointer',
                      }}
                      fallbackText="Contact email (JavaScript required)"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      component="img"
                      src={
                        themeConfig.assets?.partnerLogo ||
                        `${process.env.PUBLIC_URL}/images/r-logo.png`
                      }
                      alt="RSM Collective logo"
                      sx={{
                        width: '0.9rem',
                        height: '0.9rem',
                        objectFit: 'contain',
                        flexShrink: 0,
                        marginRight: '0.2rem',
                      }}
                    />
                    <Typography
                      component="a"
                      href="https://www.rsmc.ai/"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        trackCTA('footer_social', 'RSM Collective', {
                          destination: 'https://www.rsmc.ai/',
                          section: 'contact_info',
                        });
                      }}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '0.9rem',
                        textDecoration: 'none',
                        transition: 'color 0.2s',
                        '&:hover': {
                          color: 'rgba(255, 255, 255, 1)',
                        },
                      }}
                    >
                      RSM Collective
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconBrandLinkedin
                      size={18}
                      stroke={1.5}
                      color="#0A66C2"
                      style={{ flexShrink: 0 }}
                    />
                    <Typography
                      component="a"
                      href="https://www.linkedin.com/company/rsm-collective/"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        trackCTA('footer_social', 'LinkedIn', {
                          destination:
                            'https://www.linkedin.com/company/rsm-collective/',
                          section: 'contact_info',
                        });
                      }}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '0.9rem',
                        textDecoration: 'none',
                        transition: 'color 0.2s',
                        '&:hover': {
                          color: 'rgba(255, 255, 255, 1)',
                        },
                      }}
                    >
                      LinkedIn
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                  >
                    <IconMapPin
                      size={18}
                      stroke={1.5}
                      color={theme.custom.cream}
                      style={{ flexShrink: 0, marginTop: '1.6px' }}
                    />
                    <Typography
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '0.9rem',
                        lineHeight: 1.6,
                      }}
                    >
                      401 West A St. Ste. 200
                      <br />
                      San Diego, CA 92101
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Right Side: Nav Columns + Badges */}
            <Grid
              size={{ xs: 12, md: 'auto' }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-end' },
              }}
            >
              {/* Nav Columns Row */}
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: 4, md: 6 },
                  flexWrap: 'wrap',
                  mb: 4,
                  justifyContent: { xs: 'center', md: 'flex-end' },
                }}
              >
                {footerColumns.map((column, columnIndex) => (
                  <Box
                    key={columnIndex}
                    sx={{
                      minWidth: 120,
                      textAlign: { xs: 'center', md: 'left' },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2.5,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        color: 'rgba(255, 255, 255, 0.9)',
                      }}
                    >
                      {column.title}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                      }}
                    >
                      {column.links.map((link, linkIndex) => (
                        <Box
                          key={linkIndex}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            justifyContent: { xs: 'center', md: 'flex-start' },
                          }}
                        >
                          {link.comingSoon
                            ? <Typography
                                sx={{
                                  color: 'rgba(255, 255, 255, 0.45)',
                                  fontSize: '0.9rem',
                                  cursor: 'default',
                                }}
                              >
                                {link.text}
                                <Typography
                                  component="span"
                                  sx={{
                                    ml: 1,
                                    fontSize: '0.65rem',
                                    color: theme.custom.cream,
                                    fontStyle: 'italic',
                                    backgroundColor:
                                      'rgba(255, 255, 255, 0.12)',
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: '4px',
                                  }}
                                >
                                  Early Access
                                </Typography>
                              </Typography>
                            : link.openInNewTab
                              ? <a
                                  href={link.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    textDecoration: 'none',
                                    fontSize: '0.9rem',
                                    transition: 'color 0.2s',
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.color =
                                      'rgba(255, 255, 255, 1)')
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.color =
                                      'rgba(255, 255, 255, 0.7)')
                                  }
                                >
                                  {link.text}
                                </a>
                              : <Link
                                  to={link.href}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleLinkClick(link.href, link.text);
                                  }}
                                  style={{
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    textDecoration: 'none',
                                    fontSize: '0.9rem',
                                    transition: 'color 0.2s',
                                  }}
                                  onMouseEnter={(e) =>
                                    /** @type {HTMLElement} */ (
                                      (e.currentTarget.style.color =
                                        'rgba(255, 255, 255, 1)')
                                    )
                                  }
                                  onMouseLeave={(e) =>
                                    /** @type {HTMLElement} */ (
                                      (e.currentTarget.style.color =
                                        'rgba(255, 255, 255, 0.7)')
                                    )
                                  }
                                >
                                  {link.text}
                                </Link>}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Credential Badges - below nav columns */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'center', md: 'flex-end' },
                  pt: 2,
                }}
              >
                <Box
                  component="img"
                  src={
                    themeConfig.assets?.awsQualifiedBadge ||
                    `${process.env.PUBLIC_URL}/images/badges/aws-qualified-software-badge.png`
                  }
                  alt="AWS Qualified Software Badge"
                  sx={{
                    height: { xs: 90, md: 105 },
                  }}
                />
                <Box
                  component="img"
                  src={
                    themeConfig.assets?.awsXceleratorBadge ||
                    `${process.env.PUBLIC_URL}/images/badges/business outcomes xcelerator.png`
                  }
                  alt="Business Outcomes Xcelerator Badge"
                  sx={{
                    height: { xs: 90, md: 105 },
                  }}
                />
                <Box
                  component="img"
                  src={
                    themeConfig.assets?.finOpsBadge ||
                    `${process.env.PUBLIC_URL}/images/badges/FinOps-Badge-GeneralMember.png`
                  }
                  alt="FinOps Foundation General Member Badge"
                  sx={{
                    height: { xs: 90, md: 105 },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Bottom Bar */}
      <Box
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          py: 3,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.875rem',
                }}
              >
                &copy; 2026 RSM Collective. All rights reserved.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.4)',
                  fontSize: '0.75rem',
                  mt: 0.5,
                }}
              >
                All trademarks are the property of their respective owners.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.4)',
                  fontSize: '0.75rem',
                  mt: 0.5,
                }}
              >
                EllaFin{'\u2122'} is a trademarked product of RSM Collective
              </Typography>
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.875rem',
                textAlign: { xs: 'center', md: 'right' },
              }}
            >
              Finance-First FinOps
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default FooterNew;
